import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

const sanitizeInput = (input) => input.replace(/<[^>]*>/g, '');

function EditFirma() {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { password } = location.state || {};
    const [focusedField, setFocusedField] = useState(null);

    const [firma, setFirma] = useState(location.state?.firma || {});
    const [formData, setFormData] = useState({
        firmenname: location.state?.firma?.firmenname || '',
        adresse: location.state?.firma?.adresse || '',
        kontaktperson: location.state?.firma?.kontaktperson || '',
        email: location.state?.firma?.email || '',
        telefon: location.state?.firma?.telefon || ''
    });

    const [loading, setLoading] = useState(false);
    const [currentPassword, setPassword] = useState(password || '');
    const [errorMessages, setErrorMessages] = useState([]);
    const initialCheckDone = useRef(false);

    // Handle password state and navigation in a single effect
    useEffect(() => {
        // Only run this effect once on mount
        if (initialCheckDone.current) return;
        initialCheckDone.current = true;

        const savedPassword = sessionStorage.getItem('firmaPassword');

        // If we have a password prop, use it and save to session
        if (password) {
            sessionStorage.setItem('firmaPassword', password);
            setPassword(password);
            return;
        }

        // If no password in props but we have one in session, use it
        if (savedPassword) {
            setPassword(savedPassword);
            return;
        }

        // If no password anywhere, redirect
        alert('Kein Passwort übermittelt. Bitte versuchen Sie es erneut.');
        // Korrigierte Navigation ohne search Parameter
        navigate('/firmen-list', { replace: true });
    }, [password, navigate]);

    useEffect(() => {
        const fetchFirma = async () => {
            try {
                setLoading(true);
                const endpoint = `https://company-application-platform-backend.onrender.com/api/firma/${id}`;
                const response = await fetch(endpoint);
                if (response.ok) {
                    const data = await response.json();
                    setFirma(data);
                    setFormData({
                        firmenname: data.firmenname || '',
                        adresse: data.adresse || '',
                        kontaktperson: data.kontaktperson || '',
                        email: data.email || '',
                        telefon: data.telefon || ''
                    });
                } else {
                    alert('Firma konnte nicht geladen werden');
                    // Korrigierte Navigation ohne search Parameter
                    navigate('/firmen-list', { replace: true });
                }
            } catch (err) {
                alert('Fehler beim Laden der Firma');
                // Korrigierte Navigation ohne search Parameter
                navigate('/firmen-list', { replace: true });
            } finally {
                setLoading(false);
            }
        };

        if (!location.state?.firma && id) {
            fetchFirma();
        } else if (location.state?.firma) {
            setFirma(location.state.firma);
            setFormData({ ...location.state.firma });
        }
    }, [location.state?.firma, id, navigate]);

    const handleFocus = (fieldName) => {
        setFocusedField(fieldName);
    };

    const handleBlur = () => {
        setFocusedField(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const sanitizedValue = sanitizeInput(value);
        setFormData(prevData => ({ ...prevData, [name]: sanitizedValue }));
    };

    const validateForm = () => {
        const errors = [];
        if (!formData.firmenname.trim()) errors.push('Firmenname ist erforderlich');
        if (!formData.adresse.trim()) errors.push('Adresse ist erforderlich');
        if (!formData.email.trim()) errors.push('E-Mail ist erforderlich');
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
            errors.push('Bitte geben Sie eine gültige E-Mail-Adresse ein');
        if (!formData.telefon.trim()) errors.push('Telefonnummer ist erforderlich');

        setErrorMessages(errors);
        return errors.length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        const firmaId = firma.id || id;
        if (!firmaId) {
            setErrorMessages(['Fehler: Keine Firmen-ID gefunden.']);
            return;
        }

        const fullUpdate = {
            ...formData,
            status: firma.status,
            password: currentPassword || sessionStorage.getItem('firmaPassword')
        };

        try {
            setLoading(true);
            const endpoint = `https://company-application-platform-backend.onrender.com/api/firma/${firmaId}`;

            const response = await fetch(endpoint, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(fullUpdate),
                credentials: 'include'
            });

            if (response.ok) {
                alert('Firma erfolgreich aktualisiert!');
                if (location.pathname !== '/firmen-list') {
                    // Korrigierte Navigation ohne search Parameter
                    navigate('/firmen-list', { replace: true });
                }
            } else {
                const errorData = await response.json().catch(() => ({}));
                const errorMessage = errorData.message || 'Unbekannter Fehler beim Aktualisieren der Firma';
                setErrorMessages([`Fehler beim Speichern: ${errorMessage}`]);
            }
        } catch (err) {
            console.error('Network Error:', err);
            setErrorMessages(['Netzwerkfehler beim Aktualisieren der Firma.']);
        } finally {
            setLoading(false);
        }
    };

    if (!currentPassword) {
        return null;
    }

    return (
        <div>
            <section className="page-content-section py-8">
                <div>
                    <div className="registration-container">
                        <div className="registration-card">
                            <div className="registration-header">
                                <h1 className="registration-title">Firma bearbeiten</h1>
                                <p className="registration-subtitle">
                                    Aktualisieren Sie die Daten Ihrer registrierten Firma.
                                </p>
                            </div>
                            {errorMessages.length > 0 && (
                                <div className="error-alert">
                                    <div className="error-alert-icon">
                                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="error-alert-content">
                                        <h3 className="error-alert-title">Bitte überprüfen Sie Ihre Eingaben</h3>
                                        <ul className="error-alert-list">
                                            {errorMessages.map((error, index) => (
                                                <li key={index}>{error}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="registration-form">
                                <div className="form-grid">
                                    <div className="form-section">
                                        <div className="input-group">
                                            <label htmlFor="firmenname" className="input-label">
                                                Firmenname *
                                            </label>
                                            <div className={`input-wrapper ${focusedField === 'firmenname' ? 'focused' : ''} ${formData.firmenname ? 'has-value' : ''}`}>
                                                <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                                </svg>
                                                <input
                                                    id="firmenname"
                                                    name="firmenname"
                                                    type="text"
                                                    required
                                                    value={formData.firmenname}
                                                    onChange={handleInputChange}
                                                    onFocus={() => handleFocus('firmenname')}
                                                    onBlur={handleBlur}
                                                    className="form-input"
                                                    placeholder="Ihre Firma AG"
                                                    disabled={loading}
                                                />
                                            </div>
                                        </div>

                                        <div className="input-group">
                                            <label htmlFor="adresse" className="input-label">
                                                Adresse *
                                            </label>
                                            <div className={`input-wrapper ${focusedField === 'adresse' ? 'focused' : ''} ${formData.adresse ? 'has-value' : ''}`}>
                                                <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                </svg>
                                                <input
                                                    id="adresse"
                                                    name="adresse"
                                                    type="text"
                                                    required
                                                    value={formData.adresse}
                                                    onChange={handleInputChange}
                                                    onFocus={() => handleFocus('adresse')}
                                                    onBlur={handleBlur}
                                                    className="form-input"
                                                    placeholder="Musterstraße 123, 1234 Stadt"
                                                    disabled={loading}
                                                />
                                            </div>
                                        </div>

                                        <div className="input-group">
                                            <label htmlFor="kontaktperson" className="input-label">
                                                Kontaktperson *
                                            </label>
                                            <div className={`input-wrapper ${focusedField === 'kontaktperson' ? 'focused' : ''} ${formData.kontaktperson ? 'has-value' : ''}`}>
                                                <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                </svg>
                                                <input
                                                    id="kontaktperson"
                                                    name="kontaktperson"
                                                    type="text"
                                                    required
                                                    value={formData.kontaktperson}
                                                    onChange={handleInputChange}
                                                    onFocus={() => handleFocus('kontaktperson')}
                                                    onBlur={handleBlur}
                                                    className="form-input"
                                                    placeholder="Max Mustermann"
                                                    disabled={loading}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="form-section">
                                        <div className="input-group">
                                            <label htmlFor="email" className="input-label">
                                                E-Mail *
                                            </label>
                                            <div className={`input-wrapper ${focusedField === 'email' ? 'focused' : ''} ${formData.email ? 'has-value' : ''}`}>
                                                <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                </svg>
                                                <input
                                                    id="email"
                                                    name="email"
                                                    type="email"
                                                    required
                                                    value={formData.email}
                                                    onChange={handleInputChange}
                                                    onFocus={() => handleFocus('email')}
                                                    onBlur={handleBlur}
                                                    className="form-input"
                                                    placeholder="kontakt@firma.ch"
                                                    disabled={loading}
                                                />
                                            </div>
                                            <p className="input-hint">Nur .com oder .ch E-Mail-Adressen erlaubt</p>
                                        </div>

                                        <div className="input-group">
                                            <label htmlFor="telefon" className="input-label">
                                                Telefon *
                                            </label>
                                            <div className={`input-wrapper ${focusedField === 'telefon' ? 'focused' : ''} ${formData.telefon ? 'has-value' : ''}`}>
                                                <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                                </svg>
                                                <input
                                                    id="telefon"
                                                    name="telefon"
                                                    type="tel"
                                                    required
                                                    value={formData.telefon}
                                                    onChange={handleInputChange}
                                                    onFocus={() => handleFocus('telefon')}
                                                    onBlur={handleBlur}
                                                    className="form-input"
                                                    placeholder="+41 79 123 45 67"
                                                    disabled={loading}
                                                />
                                            </div>
                                            <p className="input-hint">Schweiz oder Deutschland mit Ländervorwahl (+41 oder +49)</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="form-footer">
                                    <div className="button-group">
                                        <button
                                            type="submit"
                                            className={`submit-button ${loading ? 'loading' : ''}`}
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <svg className="loading-spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Wird gespeichert...
                                                </>
                                            ) : 'Änderungen speichern'}
                                        </button>
                                    </div>
                                </div>
                            </form>
                            <div className="form-footer-links">
                                <button
                                    type="button"
                                    className="submit-button"
                                    onClick={() => navigate('/firmen-list')}
                                >
                                    Abbrechen
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default EditFirma;